import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { format } from 'date-fns';
import MUIAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import MUIAccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { Tooltip } from '@mui/material';

import { styled } from '@mui/material/styles';

const accordionDetailsStyles = {
  columnGap: 3,
  display: 'grid',
  gridRowGap: 16,
  gridTemplateColumns: '1fr 1fr',
};

const orDashes = R.or(R.__, '--');

const Accordion = styled(MUIAccordion)(({ theme }) => ({
  boxShadow: theme.shadows[6],
}));

const AccordionSummary = styled(MUIAccordionSummary)(({ theme }) => ({
  boxShadow: theme.shadows[2],
}));

const ShipperAllocationAccordion = ({ batchData, batchState }) => {
  const { batchOwner, availableDate, shipments } = batchState;
  const [accordionExpanded, setAccordionExpanded] = useState(true);

  const shipperIsBatchOwner = R.compose(
    R.whereEq({
      shipperIdName: batchOwner.customerIdName,
      shipperName: batchOwner.customerNameShort,
    }),
    R.defaultTo({})
  );

  const handleAccordionChange = (_, isExpanded) => {
    setAccordionExpanded(isExpanded);
  };

  const shipmentInfo = R.mergeDeepLeft(batchData?.shipments, shipments);

  return (
    <Box>
      <Accordion disableGutters expanded={accordionExpanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
          <Typography>Shipper Allocation</Typography>
        </AccordionSummary>

        <Stack divider={<Divider />}>
          {R.isNil(R.prop('shipments', batchData)) &&
            shipments.map(
              (
                {
                  shipper,
                  bookingOrigin,
                  location,
                  volume,
                  agreementCode,
                  comment,
                  defaultShipperLocation,
                },
                index
              ) => (
                // eslint-disable-next-line react/no-array-index-key
                <AccordionDetails key={index} sx={accordionDetailsStyles}>
                  {!!(shipments.length > 1) && (
                    <Typography sx={{ gridColumn: '1 / 3' }} variant="subtitle1">
                      Shipment {Number(index) + 1}
                    </Typography>
                  )}
                  <AccordionItem label="Shipper" value={shipper.shipperName} />
                  <AccordionItem label="Booking Origin" value={bookingOrigin.bookingOriginName} />
                  <AccordionItem
                    label="Requested Date"
                    value={format(new Date(availableDate), 'P')}
                  />
                  <Stack spacing={1}>
                    <Stack alignItems="center" direction="row" spacing={1}>
                      <Typography variant="caption">Default Auto-Distribution Location</Typography>
                      <Tooltip
                        arrow
                        enterTouchDelay={10}
                        placement="top"
                        title={
                          <span
                            style={{
                              display: 'block',
                              fontSize: '13px',
                              padding: '3px',
                              wordWrap: 'break-word',
                            }}
                          >
                            If you do not create a distribution for this shipment, all of the
                            barrels will auto-distribute to this defined location.
                          </span>
                        }
                      >
                        <InfoRoundedIcon fontSize="small" sx={{ color: 'graphic.main' }} />
                      </Tooltip>
                    </Stack>
                    <Typography sx={{ overflowWrap: 'anywhere' }} variant="subtitle2">
                      {orDashes(defaultShipperLocation)}
                    </Typography>
                  </Stack>
                  <AccordionItem label="Agreement Code" value={orDashes(agreementCode)} />
                  {shipperIsBatchOwner(shipper) && location != null ? (
                    <AccordionItem
                      label="Distribution Location"
                      value={orDashes(location.locationName)}
                    />
                  ) : (
                    <AccordionItem label="Distribution Location" value={orDashes('')} />
                  )}
                  <AccordionItem label="Volume" value={`${volume} bbl`} />
                  <AccordionItem label="Comment" value={orDashes(comment)} />
                </AccordionDetails>
              )
            )}
          {!R.isNil(R.prop('shipments', batchData)) &&
            Object.keys(shipmentInfo).map(index => {
              const {
                shipper,
                bookingOrigin,
                location,
                volume,
                agreementCode,
                comment = {},
                defaultShipperLocation,
              } = shipmentInfo[index];
              return (
                <AccordionDetails key={index} sx={accordionDetailsStyles}>
                  {!!(shipments.length > 1) && (
                    <Typography sx={{ gridColumn: '1 / 3' }} variant="subtitle1">
                      Shipment {Number(index) + 1}
                    </Typography>
                  )}
                  <AccordionItem
                    label="Shipment ID"
                    value={R.prop('shipmentId', shipmentInfo[index])}
                  />
                  <AccordionItem label="Booking Origin" value={bookingOrigin.bookingOriginName} />
                  <AccordionItem label="Shipper" value={shipper.shipperName} />

                  <Stack spacing={1}>
                    <Stack alignItems="center" direction="row" spacing={1}>
                      <Typography variant="caption">Default Auto-Distribution Location</Typography>
                      <Tooltip
                        arrow
                        enterTouchDelay={10}
                        placement="top"
                        title={
                          <span
                            style={{
                              display: 'block',
                              fontSize: '13px',
                              padding: '3px',
                              wordWrap: 'break-word',
                            }}
                          >
                            If you do not create a distribution for this shipment, all of the
                            barrels will auto-distribute to this defined location.
                          </span>
                        }
                      >
                        <InfoRoundedIcon fontSize="small" sx={{ color: 'graphic.main' }} />
                      </Tooltip>
                    </Stack>
                    <Typography sx={{ overflowWrap: 'anywhere' }} variant="subtitle2">
                      {orDashes(defaultShipperLocation)}
                    </Typography>
                  </Stack>
                  <AccordionItem
                    label="Requested Date"
                    value={format(new Date(availableDate), 'P')}
                  />
                  {shipperIsBatchOwner(shipper) && location != null ? (
                    <AccordionItem
                      label="Distribution Location"
                      value={orDashes(location.locationName)}
                    />
                  ) : (
                    <AccordionItem label="Distribution Location" value={orDashes('')} />
                  )}
                  <AccordionItem label="Agreement Code" value={orDashes(agreementCode)} />
                  <AccordionItem label="Comment" value={orDashes(comment)} />
                  <AccordionItem label="Volume" value={`${volume} bbl`} />
                </AccordionDetails>
              );
            })}
        </Stack>
      </Accordion>
    </Box>
  );
};

ShipperAllocationAccordion.propTypes = {
  batchData: PropTypes.shape({
    shipments: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  batchState: PropTypes.shape({
    availableDate: PropTypes.string,
    batchOwner: PropTypes.shape({
      customerIdName: PropTypes.string,
      customerNameShort: PropTypes.string,
    }),
    shipments: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

const AccordionItem = ({ label, value }) => (
  <Stack spacing={1}>
    <Typography variant="caption">{label}</Typography>
    <Typography sx={{ overflowWrap: 'anywhere' }} variant="subtitle2">
      {value}
    </Typography>
  </Stack>
);

AccordionItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

export default ShipperAllocationAccordion;
